/**
 * Typed convenience wrapper around @tanstack/react-query's useQuery for the
 * admin analytics surface.
 *
 * - Auto-wires queryKey from (key, path)
 * - Uses adminFetch so the admin token comes from the storage service
 * - 5 minute staleTime default (rollups don't change mid-period)
 * - 1 retry on AdminApiError with status != 401 (don't retry auth failures)
 *
 * Example:
 *   const { data, isLoading } = useAnalyticsQuery<Overview>(
 *     ['overview', queryString],
 *     `/api/admin/analytics/overview?${queryString}`,
 *   );
 *
 * For live strip (poll every 30s):
 *   useAnalyticsQuery<LiveStrip>(
 *     ['live-strip'],
 *     '/api/admin/analytics/live-strip',
 *     { refetchInterval: 30_000 },
 *   );
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { adminFetch, AdminApiError } from '../../../../utils/adminApi';

const DEFAULT_STALE_MS = 5 * 60 * 1000;

export type Envelope<T> = { success: boolean; data: T };

export function useAnalyticsQuery<T>(
  key: ReadonlyArray<unknown>,
  path: string,
  opts?: Omit<UseQueryOptions<Envelope<T>, AdminApiError, Envelope<T>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Envelope<T>, AdminApiError>({
    queryKey: ['analytics', ...key],
    queryFn: () => adminFetch<Envelope<T>>(path),
    staleTime: DEFAULT_STALE_MS,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) => {
      if (err instanceof AdminApiError && (err.status === 401 || err.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
    ...opts,
  });
}
